import type { JobOpportunity } from '../../state/jobOpportunities'
import {
  SIGNAL_ACTION_LABELS,
  SIGNAL_TYPE_LABELS,
  effectiveSignalAction,
  resolveWatchType,
} from '../../state/jobOpportunities'
import type { WatchType } from '../../state/jobOpportunities'

// What the Signals tables need from a job, kept out of the component so the
// column rules (what "none" means, what each column sorts by, which sub-tab a
// row is in) are testable without rendering a table.

export type SignalSortKey = 'company' | 'event' | 'role' | 'holder' | 'date' | 'action'

// "None" is an answer (the role had no previous holder); a blank is a gap
// nobody filled in. They must not read the same, which is why the cell reads
// the stored flag rather than testing for an empty name.
export const NO_PREVIOUS_HOLDER_LABEL = 'None'

export function signalHolderLabel(job: Pick<JobOpportunity, 'noPreviousHolder' | 'vacatedBy'>): string {
  if (job.noPreviousHolder === true) return NO_PREVIOUS_HOLDER_LABEL
  return (job.vacatedBy || '').trim()
}

// The Event column is what happened, with the kind of event beside it. The
// sort key is the text the reader sees first.
export function signalEventLabel(job: Pick<JobOpportunity, 'signalText' | 'signalType'>): string {
  return job.signalText || (job.signalType ? SIGNAL_TYPE_LABELS[job.signalType] || job.signalType : '')
}

export type LinkedInInput = { ok: true; value: string } | { ok: false }

/**
 * What staff type into the Company LinkedIn field, turned into what the API
 * stores. update/create-job-opportunity accept only an https URL, so a pasted
 * "linkedin.com/company/acme" gets its scheme and a pasted http:// link is
 * upgraded, rather than refusing something the reader can see is a fine link.
 * An empty field is valid and means none. Anything else with a scheme, or that
 * does not parse as a host with a dot in it, is refused here instead of
 * coming back from the server as a 400.
 */
export function normalizeCompanyLinkedInUrl(input: string): LinkedInInput {
  const trimmed = input.trim()
  if (!trimmed) return { ok: true, value: '' }
  let candidate = trimmed
  if (/^http:\/\//i.test(candidate)) candidate = `https://${candidate.slice('http://'.length)}`
  else if (!/^https:\/\//i.test(candidate)) {
    if (/^[a-z][a-z0-9+.-]*:/i.test(candidate) && !/^[^/]*\.[^/]*[:/]/.test(candidate)) return { ok: false }
    candidate = `https://${candidate}`
  }
  try {
    const url = new URL(candidate)
    if (url.protocol !== 'https:' || !url.hostname.includes('.')) return { ok: false }
    return { ok: true, value: candidate }
  } catch {
    return { ok: false }
  }
}

export function splitByWatchType(jobs: JobOpportunity[]): Record<WatchType, JobOpportunity[]> {
  const grouped: Record<WatchType, JobOpportunity[]> = { signal: [], seat: [] }
  for (const j of jobs) grouped[resolveWatchType(j)].push(j)
  return grouped
}

function sortValue(job: JobOpportunity, key: SignalSortKey): string {
  switch (key) {
    case 'company':
      return job.company || ''
    case 'event':
      return signalEventLabel(job)
    case 'role':
      return job.jobTitle || ''
    case 'holder':
      return signalHolderLabel(job)
    case 'date':
      // YYYY-MM-DD, so string order is date order.
      return job.signalObservedDate || ''
    case 'action':
      return SIGNAL_ACTION_LABELS[effectiveSignalAction(job)]
  }
}

// Rows with no value for the sorted column go last in either direction: a
// blank is missing information, not the smallest or the largest answer.
export function sortSignalRows(
  jobs: JobOpportunity[],
  key: SignalSortKey,
  order: 'asc' | 'desc'
): JobOpportunity[] {
  return jobs.slice().sort((a, b) => {
    const av = sortValue(a, key)
    const bv = sortValue(b, key)
    if (!av && !bv) return 0
    if (!av) return 1
    if (!bv) return -1
    return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
  })
}
